import {
	TransformedSuperGridColDef,
	TransformedVerticalSuperGridColDef, useForceUpdate,
} from '@aleph/supergrid';
import {
	memo, ReactElement, useEffect, useState, useMemo,
} from 'react';
import {
	LoaderBars, UndoCircle, WarningSign,
	Tooltip,
} from '@aleph/shared-components';
import { RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import { motion } from 'framer-motion';
import { ErrorBoundary } from '@sentry/react';
import { isFunction } from 'lodash';
import { ICellRendererParams } from 'ag-grid-community';
import { isActionCell } from 'wb-reddit/app/routes/superGrid/view/utils';
import useLoadingRowsStore from 'wb-reddit/app/common/components/autoNaming/loadingStore';
import classNames from 'classnames';

type OriginalRendererType = (params: ICellRendererParams) => ReactElement;

function hasColumn(params: ICellRendererParams): params is Omit<ICellRendererParams, 'column' | 'colDef'> & {
	column: NonNullable<ICellRendererParams['column']>
	colDef: NonNullable<ICellRendererParams['colDef']>
} {
	return !!params.column && !!params.colDef;
}

const RedditCellRendererWrapper = (params: ICellRendererParams & { skipCellRendererWrapper?: true, field?: string, originalRenderer?: OriginalRendererType }) => {
	const [showNonEditableTooltip, setShowNonEditableTooltip] = useState(false);
	const forceUpdate = useForceUpdate();
	const data = (params?.colDef as TransformedVerticalSuperGridColDef<RedditLevel>)?.entityData ?? params.data;
	const OriginalRenderer = params.originalRenderer;
	const { loadingRows } = useLoadingRowsStore();
	const cellRendererParams = params?.column?.getUserProvidedColDef?.()?.cellRendererParams;

	const { doUndo, isSaving } = typeof cellRendererParams === 'function' ? cellRendererParams(params) : cellRendererParams ?? {};

	const focusedCell = params.api?.getFocusedCell();
	const isCellFocused = focusedCell?.rowIndex === params.rowIndex && focusedCell?.column === params.column;

	const isLoadingRows = useMemo(() => {
		const { node, column } = params;

		const colId = column?.getColId();

		if (colId && node.id) {
			return loadingRows.columns.includes(colId) && loadingRows.rows.includes(node.id);
		}

		return false;
	}, [loadingRows]);

	const isLoading = isSaving || isLoadingRows;

	useEffect(() => {
		const handler = (e: KeyboardEvent | MouseEvent) => {
			const editableParams = { ...params };

			if (isActionCell({
				colDef: editableParams.colDef as TransformedSuperGridColDef<RedditLevel>,
				data:   editableParams.data,
			})) {
				return;
			}

			if (!hasColumn(editableParams)) {
				return;
			}

			if (e instanceof KeyboardEvent && e.key !== 'Enter') {
				return;
			}

			const editableProperty = params.colDef?.editable;
			const isEditable = isFunction(editableProperty) ? editableProperty(editableParams) : !!editableProperty;

			if (!isEditable) {
				setShowNonEditableTooltip(true);
			}

			setTimeout(() => {
				forceUpdate();
			});

			setTimeout(() => {
				setShowNonEditableTooltip(false);
			}, 1500);
		};

		params.eGridCell.addEventListener('keydown', handler);
		params.eGridCell.addEventListener('dblclick', handler);

		return () => {
			params.eGridCell.removeEventListener('keydown', handler);
			params.eGridCell.removeEventListener('dblclick', handler);
		};
	}, [params.eGridCell]);

	if (!OriginalRenderer) {
		return null;
	}

	if (params.skipCellRendererWrapper) {
		return (
			<OriginalRenderer
				{...params}
				data={data}
			/>
		);
	}

	const field = params?.field ?? params.colDef?.colId ?? params?.colDef?.field;

	const validations = params.context?.validations ?? [];
	const currValidation = validations.find(validation => (
		(validation.id === data?.id && validation.field === field)
		|| (validation.id === data?.hash && validation.field === field)
	));

	const mutationHasBeenCreated = data?.isInCreation && data?.id;

	if (currValidation) {
		return (
			<div className={classNames('flex pr-[8px] h-full w-full items-center', {
				'text-gray-600': mutationHasBeenCreated,
			})}
			>
				<div className="flex-1 h-full relative overflow-hidden">
					<OriginalRenderer
						{...params}
						data={data}
					/>
				</div>

				<div className="flex text-red-500">
					<Tooltip
						isAlwaysVisible={isCellFocused}
						backgroundColor="bg-red-500"
						showArrow
						size="large"
						placement="top-end"
						offset={16}
						content={currValidation.message}
					>
						<WarningSign
							width={params.context?.isVertical ? 16 : 12}
							height={params.context?.isVertical ? 16 : 12}
						/>
					</Tooltip>
				</div>
			</div>
		);
	}

	if (showNonEditableTooltip) {
		return (
			<div className={classNames('flex h-full w-full items-center', {
				'text-gray-600': mutationHasBeenCreated,
			})}
			>
				<div
					className="flex-1 h-full relative overflow-hidden"
				>
					<Tooltip
						isAlwaysVisible
						showArrow={false}
						placement="top"
						content="Cell is not editable"
						refElementClassNames="w-full h-full"
					>
						<OriginalRenderer
							{...params}
							data={data}
						/>
					</Tooltip>
				</div>
			</div>
		);
	}

	if (doUndo || isLoading) {
		return (
			<div className="flex pr-[8px] h-full w-full items-center">
				<div
					className="flex-1 h-full relative overflow-hidden"
				>
					<OriginalRenderer
						{...params}
						data={data}
					/>
				</div>

				<Tooltip
					placement="top"
					content={isLoading ? 'Saving...' : 'Undo'}
				>
					{
						doUndo ? <motion.button
							className="flex items-center rounded bg-white text-blue
								border-blue border reddit-cell-undo-button h-full"
							onClick={doUndo}
							initial={{ opacity: 1 }}
							animate={{ opacity: 0 }}
							transition={{ duration: 4, delay: 3 }}
						>
							<motion.div
								className="p-[4px]"
								whileHover={{
									transform: 'rotate(-180deg)',
								}}
							>
								<UndoCircle
									width={16}
									height={16}
								/>
							</motion.div>
						</motion.button> : <div className="pl-[8px] h-full flex items-center">
							<LoaderBars
								className="animate-spin"
								width={16}
								height={16}
							/>
						</div>
					}
				</Tooltip>
			</div>
		);
	}

	if (mutationHasBeenCreated) {
		return (
			<div className='text-gray-600 w-full h-full'>
				<OriginalRenderer
					{...params}
					data={data}
				/>
			</div>
		);
	}

	return (
		<OriginalRenderer
			{...params}
			data={data}
		/>
	);
};

// This is used to defer the rendering of the cell renderer until the first frame after the cell is rendered
// this gives time to ui thread to refresh the view & make it non-blocking between cell renderer renders
const PromisedCellRendererComponent = (props) => {
	const [resolved, setResolved] = useState<boolean>(props.colDef?.field === 'expand' || props?.context?.isInCreation);

	useEffect(() => {
		if (!resolved) {
			setTimeout(() => {
				setResolved(true);
			});
		}
	}, []);

	return resolved ? <RedditCellRendererWrapper {...props} /> : null;
};

export default memo(props => (
	<ErrorBoundary
		fallback={<div className="flex items-center px-[8px]">Unknown error</div>}
	>
		<PromisedCellRendererComponent
			{...props}
		/>
	</ErrorBoundary>
));
